import { useCallback, useRef } from 'react';
import { ResetPasswordDialog } from '@/components/dialogs';
import { usePasswordPromptStore } from '@/store/usePasswordPromptStore';
import { dismissPasswordPrompt } from '@/api/auth';
import { dialogMessages } from '@/constants';

/**
 * App-level host for the "Set a Password" prompt (Q-506 card 1562). Mounted
 * once in RootLayout beside AccountMergeHost, so the dialog opens over the
 * page the person has already landed on. It is the only place the dialog is
 * mounted. Not now, the X, Esc and a successful Set all close it; the person
 * stays where they are (no second navigation).
 *
 * A close without a Set clears the prompt in the same tick, THEN tells the
 * server it is dismissed (#1606), fire-and-forget: a close never waits (Q-530,
 * Q-551). The POST has its own 3 s timeout and every failure is silent. A successful Set
 * never dismisses. The first of the two wins per raise: ResetPasswordDialog
 * calls onSuccess THEN onClose after a successful Set, and that close must not
 * dismiss. One mount of `PasswordPromptDialog` is one raise, so its ref is the
 * per-arrival settle flag.
 */
export function PasswordPromptHost() {
  const pending = usePasswordPromptStore((s) => s.pending);
  if (!pending) return null;
  return <PasswordPromptDialog />;
}

function PasswordPromptDialog() {
  const clear = usePasswordPromptStore((s) => s.clear);
  const settledRef = useRef(false);

  /** A password was set: close. Never dismisses. */
  const handleSetDone = useCallback(() => {
    if (settledRef.current) return;
    settledRef.current = true;
    clear();
  }, [clear]);

  /** Any close without a Set: close now, then dismiss the prompt (best effort, never awaited). */
  const handleNotNow = useCallback(() => {
    if (settledRef.current) return;
    settledRef.current = true;
    clear();
    // Silent by design: the next sign-in ends the prompt anyway.
    void dismissPasswordPrompt().catch(() => {});
  }, [clear]);

  return (
    <ResetPasswordDialog
      open
      onClose={handleNotNow}
      onSuccess={handleSetDone}
      title={dialogMessages.passwordPrompt.title}
    />
  );
}
